import { ref } from 'vue';
import { defineStore } from 'pinia';
import { associateBy } from '@std/collections';

import {
  getGlossaryData,
  getGlossaryVersion,
  type GlossaryResponse,
} from '@/composables/network-manager';
import type { ApplicationStore } from './types';
import type { Nation, Vehicle, VehicleType } from '@/types/gloss';

import { useDogtagStore } from '@/models/dogtag-store';

const ID = 'mk-replay-app-store';
let isInit = ref(false);

/**
 * Load application store, try to load from localstorage when glossary version match
 */
export const useApplicationStore = defineStore(ID, () => {
  const store = ref<ApplicationStore>({});

  const fillData = async () => {
    const activeVersion = await getCurrentVersion();
    const storageData = loadFromLocalStorage();
    const storageVersion = storageData?.data?.version;

    if (storageData && activeVersion === storageVersion) {
      console.log('loading data from storage');
      loadDataToStore(storageData);
      return;
    }

    console.log('requesting data..');
    const { status, data, error } = getGlossaryData();

    if (error.value) throw error;

    watch(status, (newStatusValue) => {
      if (newStatusValue === 'success' && data.value) {
        loadDataToStore(data.value);
      }
    });
  };

  const loadDataToStore = (data: GlossaryResponse) => {
    store.value.nation = associateBy(data.data.nations as Nation[], (i) => i.name!);
    store.value.vehicleType = associateBy(data.data.vehicleTypes as VehicleType[], (i) => i.name!);
    store.value.vehicle = associateBy(data.data.vehicles as Vehicle[], (i) => i.id!);

    let { fillData: fillDogtagData } = useDogtagStore();
    fillDogtagData(data);

    isInit.value = true;
    saveToLocalStorage(data);
  };

  const saveToLocalStorage = (data: GlossaryResponse | null) => {
    try {
      localStorage.setItem(ID, JSON.stringify(data));
    } catch (e) {
      console.error('cannot save local storage', e);
    }
  };

  const loadFromLocalStorage = () => {
    let result: GlossaryResponse | undefined;
    try {
      const stringData = localStorage.getItem(ID);
      if (stringData) result = JSON.parse(stringData) as GlossaryResponse;
    } catch (e) {
      console.error('cannot load from local storage', e);
      return result;
    }

    return result;
  };

  const getCurrentVersion = async () => {
    return await getGlossaryVersion();
  };

  if (!isInit.value) fillData();

  const nation = computed(() => store.value.nation ?? {});
  const vehicle = computed(() => store.value.vehicle ?? {});
  const vehicleType = computed(() => store.value.vehicleType ?? {});

  return {
    nation,
    vehicle,
    vehicleType,
    state: store,
    isInit,
  };
});
